import { Mail } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface CardTemplateProps {
  message: string;
  recipientName: string;
  recipientAddress?: {
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
  signatureUrl?: string | null;
  fontFamily: string;
  fontSize: string;
  textAlignment: 'left' | 'center' | 'right';
  isPreview?: boolean; // New prop to differentiate preview from final
}

// Constants for Lob postcard dimensions (in pixels at 300dpi)
const CARD_WIDTH = 1875; // 6.25 inches * 300dpi
const CARD_HEIGHT = 1275; // 4.25 inches * 300dpi
const SAFE_AREA_MARGIN = 56.25; // 0.1875 inches * 300dpi
const ASPECT_RATIO = CARD_WIDTH / CARD_HEIGHT;

export function CardTemplate({
  message,
  recipientName,
  recipientAddress,
  signatureUrl,
  fontFamily,
  fontSize,
  textAlignment,
  isPreview = false
}: CardTemplateProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [messageHeight, setMessageHeight] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        const newScale = width / CARD_WIDTH;
        setScale(Math.min(newScale, 1));
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (messageRef.current) {
      setMessageHeight(messageRef.current.scrollHeight);
    }
  }, [message]);

  return (
    <div 
      ref={containerRef} 
      className="w-full"
      style={{
        height: `${containerWidth / ASPECT_RATIO}px`,
        maxWidth: `${CARD_WIDTH}px`,
        maxHeight: `${CARD_HEIGHT}px`,
      }}
    >
      <div 
        className="relative bg-white shadow-lg"
        style={{
          width: `${CARD_WIDTH}px`,
          height: `${CARD_HEIGHT}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Safe Area */}
        <div 
          className="absolute"
          style={{
            left: `${SAFE_AREA_MARGIN}px`,
            top: `${SAFE_AREA_MARGIN}px`,
            width: `${CARD_WIDTH - (SAFE_AREA_MARGIN * 2)}px`,
            height: `${CARD_HEIGHT - (SAFE_AREA_MARGIN * 2)}px`,
          }}
        >
          {/* Message Box with Border */}
          <div 
            className="absolute border-r-4 border-black flex flex-col"
            style={{
              left: '5px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '720px', // 2.4 inches * 300dpi
              maxHeight: '1056px', // 3.52 inches * 300dpi
            }}
          >
            {/* Message Content Container */}
            <div 
              className="flex flex-col justify-between h-full px-[2.5px] py-[12.5px]"
              style={{ minHeight: '825px' }} // 2.75 inches * 300dpi
            >
              {/* Message */}
              <div 
                ref={messageRef}
                className="overflow-y-auto scrollbar-hide"
                style={{
                  width: '690px', // 2.3 inches * 300dpi
                }}
              >
                <div 
                  className="whitespace-pre-wrap break-words"
                  style={{
                    fontFamily,
                    fontSize: `${parseInt(fontSize) * 3}px`,
                    textAlign: textAlignment,
                    lineHeight: '1.6',
                    letterSpacing: '0.01em'
                  }}
                >
                  {message}
                </div>
              </div>

              {/* Signature Area */}
              {signatureUrl && (
                <div className="mt-6">
                  <p className="mb-2" style={{ 
                    fontFamily,
                    fontSize: `${parseInt(fontSize) * 3}px`,
                    lineHeight: '1.6',
                    letterSpacing: '0.01em'
                  }}>Best wishes,</p>
                  <img 
                    src={signatureUrl} 
                    alt="Signature" 
                    className="max-w-full max-h-[180px] object-contain object-left-bottom"
                    style={{ fontFamily }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Side Content */}
          <div 
            className="absolute"
            style={{
              top: 0,
              right: 0,
              width: `${CARD_WIDTH - (SAFE_AREA_MARGIN * 2) - 720 - 10}px`,
              height: '100%',
            }}
          >
            {/* Cardible Branding */}
            <div 
              className="absolute top-[15%] left-0 w-full flex flex-col items-center"
              style={{ transform: 'scale(1.1)' }}
            >
              <p className="text-2xl tagline text-brand-primary">Sent with Love.</p>
              <Mail className="w-10 h-10 sm:w-12 sm:h-12 text-brand-primary" />
              <h2 className="text-brand-secondary text-4xl font-semibold font-display mb-2">
                Cardible.io
              </h2>
            </div>

            {/* No Ink Zone - Recipient Address (Preview Only) */}
            {isPreview && (
              <div 
                className="absolute bottom-[82.5px] right-[82.5px] w-[1200px] h-[712.5px] flex flex-col items-center justify-center"
                style={{
                  border: '1px dashed rgba(0,0,0,0.1)'
                }}
              >
                <div className="text-center">
                  <p style={{ fontFamily, fontSize: '36px', marginBottom: '8px' }}>{recipientName}</p>
                  {recipientAddress?.address_line1 && (
                    <p style={{ fontFamily, fontSize: '36px', marginBottom: '8px' }}>{recipientAddress.address_line1}</p>
                  )}
                  {(recipientAddress?.address_city || recipientAddress?.address_state || recipientAddress?.address_zip) && (
                    <p style={{ fontFamily, fontSize: '36px' }}>
                      {[
                        recipientAddress.address_city,
                        recipientAddress.address_state,
                        recipientAddress.address_zip
                      ].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 